import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Smartphone,
  Download,
  Save,
  Link as LinkIcon,
} from "lucide-react";

const WEB_URL = (process.env.REACT_APP_WEB_URL || "https://bbcbarani.org").replace(/\/$/, "");
const DEFAULT_ANDROID_URL = `${WEB_URL}/downloads/BBCconnect.apk`;

export function MobileAppTab() {
  const [loading, setLoading] = useState(true);
  const [savingAndroid, setSavingAndroid] = useState(false);
  const [savingIos, setSavingIos] = useState(false);

  // Android
  const [androidUrl, setAndroidUrl] = useState(DEFAULT_ANDROID_URL);
  const [androidVersion, setAndroidVersion] = useState("");
  const [androidNotes, setAndroidNotes] = useState("");
  const [androidEnabled, setAndroidEnabled] = useState(true);

  // iOS / PWA
  const [iosUrl, setIosUrl] = useState(WEB_URL);
  const [iosNote, setIosNote] = useState(
    "Open in Safari, tap Share, then select Add to Home Screen."
  );

  const load = async () => {
    try {
      const r = await api.get("/content/mobile_app");
      const data = r.data || {};

      setAndroidUrl(data.android_url || DEFAULT_ANDROID_URL);
      setAndroidVersion(data.android_version || "");
      setAndroidNotes(data.android_release_notes || "");
      setAndroidEnabled(data.android_enabled !== false);

      setIosUrl(data.ios_url || WEB_URL);
      setIosNote(
        data.ios_install_note ||
          "Open in Safari, tap Share, then select Add to Home Screen."
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to load mobile app settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const currentPayload = () => ({
    android_url: androidUrl.trim(),
    android_version: androidVersion.trim(),
    android_release_notes: androidNotes.trim(),
    android_enabled: androidEnabled,

    ios_url: iosUrl.trim(),
    ios_install_note: iosNote.trim(),
  });

  const saveAndroid = async () => {
    if (!androidUrl.trim()) {
      toast.error("APK download URL is required");
      return;
    }

    if (!androidVersion.trim()) {
      toast.error("Android version is required");
      return;
    }

    if (!/^https:\/\//i.test(androidUrl.trim())) {
      toast.error("APK URL must start with https://");
      return;
    }

    setSavingAndroid(true);

    try {
      await api.put("/content/mobile_app", currentPayload());
      toast.success("Android download settings saved");
      await load();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.detail ||
          "Failed to save Android settings"
      );
    } finally {
      setSavingAndroid(false);
    }
  };

  const saveIos = async () => {
    if (!iosUrl.trim()) {
      toast.error("PWA URL is required");
      return;
    }

    setSavingIos(true);

    try {
      await api.put("/content/mobile_app", currentPayload());
      toast.success("iOS/PWA settings saved");
      await load();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.detail ||
          "Failed to save iOS settings"
      );
    } finally {
      setSavingIos(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-[#5C6661]">
        Loading settings...
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="mobile-app-tab">
      <div className="grid lg:grid-cols-2 gap-8">

        {/* Android */}
        <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl shadow-sm">
          <h3 className="font-serif-display text-2xl text-[#1C201E] mb-6 flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-[#2C4C3B]" />
            Android Application
          </h3>

          <div className="space-y-5">

            <div className="space-y-1">
              <label className="text-xs font-bold text-[#5C6661]">
                APK Download URL
              </label>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LinkIcon className="h-4 w-4 text-gray-400" />
                </div>

                <input
                  type="url"
                  value={androidUrl}
                  onChange={(e) => setAndroidUrl(e.target.value)}
                  placeholder={DEFAULT_ANDROID_URL}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm"
                />
              </div>

              <p className="text-[10px] text-[#5C6661]">
                Enter the direct downloadable APK link. No APK upload is required.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-[#5C6661]">
                Version
              </label>

              <input
                type="text"
                value={androidVersion}
                onChange={(e) => setAndroidVersion(e.target.value)}
                placeholder="e.g. 0.4.0"
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-[#5C6661]">
                Release Notes
              </label>

              <textarea
                rows={4}
                value={androidNotes}
                onChange={(e) => setAndroidNotes(e.target.value)}
                placeholder="What's new in this version?"
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm resize-none"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={androidEnabled}
                onChange={(e) => setAndroidEnabled(e.target.checked)}
                className="rounded border-[#E5E0D8] text-[#2C4C3B] focus:ring-[#D4A373]"
              />

              <span className="text-xs text-[#1C201E]">
                Show Android download on website
              </span>
            </label>

            <button
              type="button"
              onClick={saveAndroid}
              disabled={savingAndroid}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#2C4C3B] text-white font-bold hover:bg-[#1A3326] transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {savingAndroid
                ? "Saving..."
                : "Save Android Settings"}
            </button>

            {androidUrl && (
              <div className="mt-6 p-5 bg-[#F3EFEA] rounded-2xl border border-[#D4A373]/30">
                <p className="text-[10px] font-bold text-[#D4A373] uppercase tracking-widest">
                  Current Android Release
                </p>

                <h4 className="mt-1 text-lg font-bold text-[#1C201E]">
                  {androidVersion
                    ? `Version ${androidVersion}`
                    : "Version not specified"}
                </h4>

                <code className="mt-3 text-[10px] bg-white px-2 py-2 rounded border border-[#E5E0D8] block break-all text-[#2C4C3B]">
                  {androidUrl}
                </code>

                <a
                  href={androidUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-[#2C4C3B] hover:underline"
                >
                  <Download className="w-4 h-4" />
                  Test Download Link
                </a>
              </div>
            )}
          </div>
        </div>

        {/* iOS / PWA */}
        <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl shadow-sm">
          <h3 className="font-serif-display text-2xl text-[#1C201E] mb-6 flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-[#007AFF]" />
            iOS / PWA Application
          </h3>

          <div className="space-y-5">

            <div className="space-y-1">
              <label className="text-xs font-bold text-[#5C6661]">
                PWA URL
              </label>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LinkIcon className="h-4 w-4 text-gray-400" />
                </div>

                <input
                  type="url"
                  value={iosUrl}
                  onChange={(e) => setIosUrl(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm"
                  placeholder={WEB_URL}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-[#5C6661]">
                Installation Instruction
              </label>

              <textarea
                rows={4}
                value={iosNote}
                onChange={(e) => setIosNote(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:ring-2 focus:ring-[#D4A373] outline-none text-sm resize-none"
              />
            </div>

            <button
              type="button"
              onClick={saveIos}
              disabled={savingIos}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#2C4C3B] text-white font-bold hover:bg-[#1A3326] transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {savingIos
                ? "Saving..."
                : "Save iOS Settings"}
            </button>

            <div className="mt-6 p-6 rounded-2xl bg-blue-50 border border-blue-100">
              <div className="flex flex-col items-center text-center p-4 bg-white rounded-xl shadow-sm">
                <img
                  src="/IOS.webp"
                  alt="iOS"
                  className="w-12 h-12 mb-3"
                />

                <p className="text-sm font-bold text-[#1C201E]">
                  BBC Connect
                </p>

                <a
                  href={iosUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 text-xs text-[#007AFF] font-bold hover:underline"
                >
                  {iosUrl}
                </a>

                <p className="mt-2 text-[10px] text-gray-500 italic">
                  "{iosNote}"
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default MobileAppTab;
